/** @jsx jsx */
/** @jsxFrag React.Fragment */
import type { SerializedStyles } from '@emotion/react';
import { jsx } from '@emotion/react';

import * as React from 'react';

import NavigationButtons from 'bundles/ai-coach-platform/components/patterns/chat/recommendations/NavigationButtons';
import RecommendedActions from 'bundles/ai-coach-platform/components/patterns/chat/recommendations/RecommendedActions';
import type { SendMessageFunc } from 'bundles/ai-coach-platform/components/patterns/chat/types';

import _t from 'i18n!nls/ai-coach-platform';

import type { MessageTypes } from '../store/types';

type Props = {
  message: MessageTypes;
  sendMessage: SendMessageFunc;
  showRecommendedActions: boolean;
  recommendedActionsLabel?: string;
  onRecommendedActionClick?: () => void;
  /**
   * Optional caller-supplied styles applied on top of each recommended action button's default styles.
   * Temporary prop for the Two-Panel XDP experiment (MERCH-2963). Please consult the platform team before using it in your project.
   */
  recommendedActionButtonCustomStyles?: SerializedStyles;
};

/**
 * Renders navigation buttons based on the chat message.
 *
 * @param {Props} props - The properties object.
 * @param {MessageTypes} props.message - The message object containing navigation buttons.
 */
export const ChatRecommendations = (props: Props): React.ReactElement | null => {
  const {
    message: { navigationButtons, recommendedActions, timestamp },
    sendMessage,
    showRecommendedActions,
    recommendedActionsLabel,
    onRecommendedActionClick,
    recommendedActionButtonCustomStyles,
  } = props;

  return (
    <>
      {navigationButtons?.length ? (
        <div role="group" aria-label={_t('Navigation buttons')}>
          {/*
           // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess */}
          <NavigationButtons navigationButton={navigationButtons[0]} />
        </div>
      ) : null}
      {recommendedActions?.length && showRecommendedActions ? (
        <div role="group" aria-label={_t('Recommended actions')}>
          <RecommendedActions
            recommendedActions={recommendedActions}
            sendMessage={sendMessage}
            timestamp={timestamp}
            recommendedActionsLabel={recommendedActionsLabel}
            onRecommendedActionClick={onRecommendedActionClick}
            recommendedActionButtonCustomStyles={recommendedActionButtonCustomStyles}
            variant="cds-labs"
          />
        </div>
      ) : null}
    </>
  );
};

export default ChatRecommendations;
